import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { SquareChartGantt, Flag, Settings, Zap, X } from "lucide-react";
import { browserHistory } from "src/helpers/history";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import styles from "../../styles/automation-styles";
import AutomationTemplateCard from "./automation-template-card";

const templateInformations = [
	{
		"id": 1,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se o ticket pertencer a uma Organização específica.",
		"automationactions": "Enviar e-mail para o solicitante."
	},
	{
		"id": 2,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se a criação do ticket ocorrer durante um horário específico (fora do comercial).",
		"automationactions": "Enviar e-mail para o solicitante."
	},
	{
		"id": 3,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se o Título, E-mail, Nome do Solicitante, Organização ou Tipo de Chamado coincidirem.",
		"automationactions": "Atribuir a um Time específico."
	},
	{
		"id": 4,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se o Título, Nome do Solicitante, Organização ou Tipo de Chamado atenderem aos critérios.",
		"automationactions": "Definir a prioridade como Alta."
	},
	{
		"id": 5,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se o Título contiver termos de spam ou o E-mail do Solicitante estiver na lista.",
		"automationactions": "Adicionar a tag 'SPAM'."
	},
	{
		"id": 6,
		"automationevent": "Quando um novo ticket é criado.",
		"automationconditions": "Se o Anexo não estiver presente e o Chamado pertencer a uma Organização ou Categoria específica.",
		"automationactions": "Enviar e-mail para o solicitante e Definir status como 'Aguardando cliente'."
	},
	{
		"id": 8,
		"automationevent": "Verificação horária de antiguidade do ticket.",
		"automationconditions": "Se o Status estiver como 'Aguardando cliente' e o tempo desde a resposta do agente for superior a 24h.",
		"automationactions": "Enviar e-mail de lembrete para o solicitante."
	},
	{
		"id": 9,
		"automationevent": "Verificação horária de antiguidade do ticket.",
		"automationconditions": "Se o tempo desde o encerramento ou desde a última resposta do agente atingir o limite.",
		"automationactions": "Definir status como 'Encerrado'."
	},
	{
		"id": 13,
		"automationevent": "Quando um novo acompanhamento é adicionado ao ticket.",
		"automationconditions": "Se as interações do solicitante estiverem entre os limites definidos e a Origem for compatível.",
		"automationactions": "Enviar e-mail para o agente e Atribuir a um Agente específico."
	},
	{
		"id": 14,
		"automationevent": "Falha detectada no envio de e-mail para o solicitante.",
		"automationconditions": "Sempre que ocorrer um erro de entrega na comunicação.",
		"automationactions": "Definir status, Notificar agente por e-mail e Adicionar tag."
	},
	{
		"id": 15,
		"automationevent": "Verificação horária de tickets (DE -> PARA).",
		"automationconditions": "Se o tempo desde que o ticket venceu (SLA) for superior ao limite configurado.",
		"automationactions": "Enviar e-mail para o agente e Reatribuir o Agente."
	},
	{
		"id": 17,
		"automationevent": "Quando uma avaliação de satisfação é recebida.",
		"automationconditions": "Se o ticket pertencer a uma Organização específica após a avaliação.",
		"automationactions": "Atribuir a um Time e Enviar e-mail para o agente."
	}
];

// Main AutomationTemplateList Component
const AutomationTemplateList = (props) => {
	const [activeTab, setActiveTab] = useState(parseInt(localStorage.getItem("activeAutomationTemplateTab")) || 0);
	const [searchTerm, setSearchTerm] = useState("");
	const [automationPreviewState, setAutomationPreviewState] = React.useState({ open: false, data: null, loading: false });

	const handleTabClick = (tabIndex) => {
		setActiveTab(tabIndex);
		localStorage.setItem("activeAutomationTemplateTab", tabIndex);
	};

	function handleOpenInformationAutomationTemplate(data = null) {
		setAutomationPreviewState({ open: !automationPreviewState.open, data });
	};

	function handleGoToEditAutomationTemplate(data) {
		props.handleGoToEditAutomationTemplate(data);

		handleOpenInformationAutomationTemplate(null)
	}

	// Get automationTemplates for current active tab and filter by search term
	const automationTemplates = (props.data[activeTab] || []).filter((automationTemplate) => {
		if (!searchTerm) return true;

		const searchLower = searchTerm.toLowerCase();
		return (
			automationTemplate.title.toLowerCase().includes(searchLower) ||
			automationTemplate.condition.toLowerCase().includes(searchLower)
		);
	});

	function renderInformationAutomationTemplatePreview() {
		const data = automationPreviewState.data || {};

		if (!data || Object.keys(data).length === 0 && !automationPreviewState.loading) return null;

		const title = `${data.name}` || "";
		const subtitle = data.description || "";

		const automationtypeid = data.automationtypeid ? data.automationtypeid : null;
		const isEventType = automationtypeid === 2;

		//NEW INFORMATIONS
		const informations = templateInformations.find(item => item.id === data.id);

		//EVENTS
		const automationeventdetails = data.automationeventdetails || [];
		const automationevent = informations?.automationevent || automationeventdetails.map(detail => detail.automationevent.name || null).filter(name => name).join(", ");;

		//FILTERS
		const automationfilters = data.automationfilters || [];
		const customAutomationconditions = automationfilters.map(filter => {
			const conditions = filter.automationconditions.map(condition => {
				const automationconditiontype = condition.automationconditiontype.name || null;
				const automationconditioncriteria = condition.automationconditioncriteria.name || null;
				const automationoperator = condition.automationoperator.name || null;

				return automationconditiontype ? `${automationconditiontype} ${automationconditioncriteria} ${automationoperator}` : null;
			}).filter(name => name).join("/n");

			return conditions
		}).join("; ");
		const automationconditions = informations?.automationconditions || customAutomationconditions;

		//ACTIONS
		const automationactiondetails = data.automationactiondetails || [];
		const automationactions = informations?.automationactions || automationactiondetails.map(detail => detail.automationaction.name || null).filter(name => name).join(", ");

		return (
			<Grid
				key={data.id}
				container
				role="presentation"
				onKeyDown={() => handleOpenInformationAutomationTemplate(null)}
				sx={{
					height: "100%",
				}}
			>
				<Grid item xs={0.7}>
					<Box
						display="flex"
						bgcolor="white"
						alignItems="baseline"
						height="100%"
					>
						<IconButton aria-label="close" size="small" onClick={() => handleOpenInformationAutomationTemplate(null)}>
							<X size={21} />
						</IconButton>
					</Box>
				</Grid>

				<Grid item xs={11.3}>
					<Box
						display="flex"
						flexDirection="column"
						bgcolor="white"
						width="100%"
						height="100%"
						justifyContent="space-between"
						pt={4}
						pr={4}
						pl={2}
						pb={2}
					>
						<Box
							display="flex"
							flexDirection="column"
							gap={1}
						>
							<Box display="flex" alignItems="center" gap={0.5} mb={2}>
								<SquareChartGantt size={18} color="gray" />

								<Typography variant="body2" color="text.secondary">
									Regra que é executada na criação do ticket:
								</Typography>
							</Box>

							{automationPreviewState.loading && (
								<Box display="flex" justifyContent="center" alignItems="center" height="200px">
									<CircularProgress color='secondary' />
								</Box>
							)}

							{!automationPreviewState.loading && (
								<>
									<Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
										{title}
									</Typography>

									<Typography variant="body2" color="text.secondary">
										{subtitle}
									</Typography>


									<Paper elevation={0} sx={{ border: '1px solid #e6e9ee', borderRadius: 2, p: 2, mt: 4 }}>
										<Grid container direction="column" spacing={2}>

											{isEventType &&
												<Grid item>
													<Grid container alignItems="flex-start" spacing={2}>
														<Grid item>
															<Box sx={{ width: 36, height: 36, bgcolor: '#f4fbff', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
																<Flag size={18} />
															</Box>
														</Grid>

														<Grid item xs>
															<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Eventos</Typography>
															{automationevent?.split("/n").map((event, index) => (
																<Typography key={index} variant="body2" color="text.secondary">{event}</Typography>
															))}
														</Grid>
													</Grid>
												</Grid>
											}

											<Grid item>
												<Grid container alignItems="flex-start" spacing={2}>
													<Grid item>
														<Box sx={{ width: 36, height: 36, bgcolor: '#fff8ef', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
															<Settings size={18} />
														</Box>
													</Grid>
													<Grid item xs>
														<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Filtros</Typography>
														{automationconditions?.split("/n").map((condition, index) => (
															<Typography key={index} variant="body2" color="text.secondary">{condition}</Typography>
														))}
													</Grid>
												</Grid>
											</Grid>

											<Grid item>
												<Grid container alignItems="flex-start" spacing={2}>
													<Grid item>
														<Box sx={{ width: 36, height: 36, bgcolor: '#f5fff6', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
															<Zap size={18} />
														</Box>
													</Grid>

													<Grid item xs>
														<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ações</Typography>
														{automationactions?.split("/n").map((action, index) => (
															<Typography key={index} variant="body2" color="text.secondary">{action}</Typography>
														))}
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Paper>
								</>
							)}
						</Box>

						<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
							<Button
								style={styles.newRuleButton}
								onClick={() => handleOpenInformationAutomationTemplate(null)}>
								<span>Cancelar</span>
							</Button>

							<Button
								style={styles.newRuleButton}
								onClick={(e) => {
									e.preventDefault();
									handleGoToEditAutomationTemplate(data);
								}}
								disabled={automationPreviewState.loading}
							>
								<span>Usar Template</span>
							</Button>
						</Box>
					</Box>
				</Grid>
			</Grid>
		)
	}

	useEffect(() => {
		// Quando o Drawer for aberto, faz a request
		if (automationPreviewState.data && automationPreviewState.open) {
			const fetchData = async () => {
				try {
					setAutomationPreviewState((prevState) => ({
						...prevState,
						loading: true,
					}));

					const automationTemplateSelected = automationPreviewState.data

					const resultDataToUpdate = await props.getItemForUpdateAsync(automationTemplateSelected.id, false);

					setAutomationPreviewState((prevState) => ({
						...prevState,
						data: resultDataToUpdate,
						loading: false,
					}));

				} catch (err) {
					console.error("Erro ao carregar dados:", err);
				}
			};

			fetchData();
		}
	}, [automationPreviewState.open]);

	return (
		<div style={styles.contentWrapper}>
			<Box pb={2}>
				<Button
					variant="outlined"
					color="primary"
					onClick={browserHistory.goBack}
				>
					{<FormattedMessage id="back" />}
				</Button>
			</Box>

			<h1 style={styles.heading}>Explore templates</h1>

			<Grid container spacing={6} pt={2}>
				<Grid item xs={2.5}>
					{/* Tab Buttons */}
					<Grid
						container
						spacing={1}
						direction="column"
						alignItems="flex-start"
					>
						<Grid item xs={12} style={{ width: '100%' }}>
							<ListItemButton
								sx={{
									width: '100%', justifyContent: 'space-between', borderRadius: '8px', transition: 'all 0.3s ease',
									...(activeTab === 0 ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {})
								}}
								onClick={() => handleTabClick(0)}
							>
								Todos

								<span style={styles.tabCount}>{props.data[0]?.length}</span>
							</ListItemButton>
						</Grid>

						<Grid item xs={12} style={{ width: '100%' }}>
							<ListItemButton
								sx={{
									width: '100%', justifyContent: 'space-between', borderRadius: '8px', transition: 'all 0.3s ease',
									...(activeTab === 1 ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {})
								}}
								onClick={() => handleTabClick(1)}
							>
								Criação De Tickets

								<span style={styles.tabCount}>{props.data[1]?.length}</span>
							</ListItemButton>
						</Grid>

						<Grid item xs={12} style={{ width: '100%' }}>
							<ListItemButton
								sx={{
									width: '100%', justifyContent: 'space-between', borderRadius: '8px', transition: 'all 0.3s ease',
									...(activeTab === 2 ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {})
								}}
								onClick={() => handleTabClick(2)}
							>
								Atualizações De Tickets

								<span style={styles.tabCount}>{props.data[2]?.length}</span>
							</ListItemButton>
						</Grid>

						<Grid item xs={12} style={{ width: '100%' }}>
							<ListItemButton
								sx={{
									width: '100%', justifyContent: 'space-between', borderRadius: '8px', transition: 'all 0.3s ease',
									...(activeTab === 3 ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {})
								}}
								onClick={() => handleTabClick(3)}
							>
								Acionadores Horários

								<span style={styles.tabCount}>{props.data[3]?.length}</span>
							</ListItemButton>
						</Grid>

					</Grid>
				</Grid>

				<Grid item xs={9.5}>
					<p style={styles.infoText}>
						Os modelos são automações pré-construídas que você pode usar com um clique de um botão.
						Selecionamos as automações mais utilizadas para você.
					</p>

					<Grid container spacing={3}>
						{automationTemplates.length > 0 ? (
							automationTemplates.map((automationTemplate) => (
								<AutomationTemplateCard
									key={automationTemplate.id}
									automationTemplate={automationTemplate}
									handleClickPreview={(data) => {
										handleOpenInformationAutomationTemplate(data)
									}}
								/>
							))
						) : (
							<div style={{ ...styles.emptyContainer, marginTop: '32px' }}>
								<p style={styles.emptyText}>
									{searchTerm
										? "Nenhum template de automação encontrado com os termos de pesquisa."
										: "Não existem templates de automação nesta categoria. Clique em 'Novo Template' para criar um."}
								</p>
							</div>
						)}
					</Grid>
				</Grid>
			</Grid>

			<Drawer
				anchor={"right"}
				open={automationPreviewState.open}
				onClose={() => handleOpenInformationAutomationTemplate(null)}
				sx={{
					'& .MuiDrawer-paperAnchorRight .MuiIconButton-root, & .MuiDrawer-paperAnchorRight button': {
						color: "#8bc34a !important",
					},
				}}
				PaperProps={{
					sx: {
						width: 520
					},
				}}
			>
				{renderInformationAutomationTemplatePreview()}
			</Drawer>
		</div>
	);
};
export default AutomationTemplateList;